import { z } from 'zod';
import { AiDraftProvenanceSchema, GeneratedScenarioDraftSchema } from './scenarios';
import { ScenarioProposalEvaluationSchema } from './scenario-evaluation';

/** Bounded generation limits — Feature #3. */
export const REPO_SCENARIO_MAX_KNOWLEDGE_ITEMS = 12;
export const REPO_SCENARIO_MAX_DRAFTS_PER_ITEM = 3;
export const REPO_SCENARIO_MAX_TOTAL_DRAFTS = 24;

export const RepoScenarioSuggestionStatusSchema = z.enum(['draft', 'reviewed', 'converted', 'rejected']);
export type RepoScenarioSuggestionStatus = z.infer<typeof RepoScenarioSuggestionStatusSchema>;

export const RepoKnowledgeKnownFactSchema = z.object({
  kind: z.enum(['route', 'api', 'workspace', 'source', 'glob', 'discovery']),
  label: z.string().min(1).max(120),
  value: z.string().min(1).max(500),
});
export type RepoKnowledgeKnownFact = z.infer<typeof RepoKnowledgeKnownFactSchema>;

export const RepoKnowledgeScenarioContextSchema = z.object({
  proposalId: z.string().uuid(),
  proposalType: z.enum(['route', 'api']),
  workspaceUnit: z.string(),
  route: z.string().optional(),
  apiPath: z.string().optional(),
  apiMethod: z.string().optional(),
  sourcePath: z.string(),
  sourceLine: z.number().int().positive().optional(),
  glob: z.string().optional(),
  extractorId: z.string(),
  evidenceBand: z.string(),
  knownFacts: z.array(RepoKnowledgeKnownFactSchema).max(20),
  relatedDiscoveryTitle: z.string().max(120).optional(),
});
export type RepoKnowledgeScenarioContext = z.infer<typeof RepoKnowledgeScenarioContextSchema>;

export const RepoScenarioSuggestionSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  repoProposalId: z.string().uuid(),
  status: RepoScenarioSuggestionStatusSchema,
  title: z.string().min(1).max(200),
  description: z.string().max(2_000).optional(),
  workspaceUnit: z.string(),
  routeOrApi: z.string(),
  sourcePath: z.string(),
  knownFacts: z.array(RepoKnowledgeKnownFactSchema),
  assumptions: z.array(z.string().max(300)).max(20),
  draft: GeneratedScenarioDraftSchema,
  evaluation: ScenarioProposalEvaluationSchema.optional(),
  provenance: AiDraftProvenanceSchema,
  convertedScenarioId: z.string().uuid().nullable().optional(),
  updatedAt: z.string(),
});
export type RepoScenarioSuggestion = z.infer<typeof RepoScenarioSuggestionSchema>;

export const ListRepoScenarioSuggestionsResponseSchema = z.object({
  projectId: z.string().uuid(),
  suggestions: z.array(RepoScenarioSuggestionSchema),
  draftCount: z.number().int().min(0),
  recommendationsAreNotTruth: z.literal(true),
});
export type ListRepoScenarioSuggestionsResponse = z.infer<typeof ListRepoScenarioSuggestionsResponseSchema>;

export const GenerateRepoScenarioSuggestionsRequestSchema = z.object({
  proposalIds: z.array(z.string().uuid()).max(REPO_SCENARIO_MAX_KNOWLEDGE_ITEMS).optional(),
  businessFlowId: z.string().uuid().optional(),
  /** When `deterministic`, uses confirmed-facts-only draft builder — no LLM/heuristic AI path. */
  generationMode: z.enum(['ai', 'deterministic']).default('ai'),
});
export type GenerateRepoScenarioSuggestionsRequest = z.infer<typeof GenerateRepoScenarioSuggestionsRequestSchema>;

export const GenerateRepoScenarioSuggestionsResponseSchema = z.object({
  projectId: z.string().uuid(),
  suggestions: z.array(RepoScenarioSuggestionSchema),
  createdCount: z.number().int().min(0),
  skippedDuplicateCount: z.number().int().min(0),
  generationSource: z.enum(['llm', 'heuristic', 'deterministic']),
  recommendationsAreNotTruth: z.literal(true),
});
export type GenerateRepoScenarioSuggestionsResponse = z.infer<typeof GenerateRepoScenarioSuggestionsResponseSchema>;

export const ReviewRepoScenarioSuggestionRequestSchema = z.object({
  status: z.enum(['reviewed', 'rejected']),
});
export type ReviewRepoScenarioSuggestionRequest = z.infer<typeof ReviewRepoScenarioSuggestionRequestSchema>;

export const ConvertRepoScenarioSuggestionRequestSchema = z.object({
  businessFlowId: z.string().uuid(),
});
export type ConvertRepoScenarioSuggestionRequest = z.infer<typeof ConvertRepoScenarioSuggestionRequestSchema>;

export const ConvertRepoScenarioSuggestionResponseSchema = z.object({
  suggestion: RepoScenarioSuggestionSchema,
  scenarioId: z.string().uuid(),
});
export type ConvertRepoScenarioSuggestionResponse = z.infer<typeof ConvertRepoScenarioSuggestionResponseSchema>;
